import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UtilsService } from 'src/app/shared/utils/utils.service';
import { logisticaModulo as diccionarios } from '../../logistica-diccionario';
import { LogisticaService } from '../../service/logistica.services';
import { ICargaMasivaResultado, IEstadoDepuracion, ILogDepuracionPedido } from '../../model/log-depuracion-pedidos.model';

@Component({
  selector: 'app-log-pedido-depura',
  templateUrl: './log-pedido-depura.component.html',
  styleUrls: ['./log-pedido-depura.component.css']
})
export class LogPedidoDepuraComponent implements OnInit, OnDestroy {
  public DAT_TITULOS    = diccionarios.titulos.depuracionPedidos;
  public GLOBAL_TITULOS = diccionarios.titulos.global;

  public readonly ESTADO_PENDIENTE = 'Pendiente';
  public readonly ESTADO_CONCLUIDO = 'Concluido';
  public readonly PAGE_SIZE = 50;
  public readonly PLAN_MAX = 2000;

  public loading      = false;
  public errorCarga   = false;
  public guardando    = false;
  public procesandoCarga = false;
  public exportando   = false;

  public registros    : ILogDepuracionPedido[] = [];
  public filtrados    : ILogDepuracionPedido[] = [];
  public pagina       = 1;

  public textoBuscar  = '';
  public estados      : IEstadoDepuracion[] = [];
  public filtroEstado = this.ESTADO_PENDIENTE;
  public filtroFuente = '';
  public fuentes      : string[] = [];

  public seleccionado : ILogDepuracionPedido | null = null;
  public titleModalPlan  = 'Plan de Acción';
  public titleModalCarga = 'Carga Masiva de Plan de Acción';
  public formPlan: FormGroup;

  public archivoCsv: File | null = null;
  public textoCsv = '';
  public arrastrando = false;
  public resultadoCarga: ICargaMasivaResultado | null = null;

  private usuario: string;
  private buscar$ = new Subject<string>();
  private subs = new Subscription();

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private logisticaApi: LogisticaService,
    private utilServices: UtilsService,
  ) { }

  ngOnInit(): void {
    this.usuario = JSON.parse(localStorage.getItem('datosUsuarioLogin'))?.login2;
    this.formPlan = this.fb.group({
      planAccion: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(this.PLAN_MAX)]],
      estado: [this.ESTADO_CONCLUIDO, Validators.required],
    });
    this.subs.add(
      this.buscar$.pipe(debounceTime(250), distinctUntilChanged()).subscribe(() => this.aplicarFiltros())
    );
    this.cargarEstados();
    this.listar();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /************** LISTADO Y FILTROS ******** */
  cargarEstados() {
    this.logisticaApi.getDepuracionEstados().subscribe(
      (res: IEstadoDepuracion[]) => this.estados = res || [],
      (err) => {
        console.error('Error cargando estados de depuración', err);
        this.estados = [{ key: 'PENDIENTE', value: this.ESTADO_PENDIENTE }];
      }
    );
  }

  listar() {
    this.loading = true;
    this.errorCarga = false;
    this.logisticaApi.getDepuracionByEstado(this.filtroEstado).subscribe(
      (res: ILogDepuracionPedido[]) => {
        this.registros = res || [];
        this.fuentes = [...new Set(this.registros.map(r => r.fuente).filter(f => !!f))].sort();
        this.loading = false;
        this.aplicarFiltros();
      },
      (err) => {
        this.loading = false;
        this.errorCarga = true;
        this.registros = [];
        this.filtrados = [];
        console.error('Error cargando depuración de pedidos', err);
        this.utilServices.mensajeErroToast('No se pudo cargar el listado de depuración');
      }
    );
  }

  onBuscar(valor: string) {
    this.textoBuscar = valor;
    this.buscar$.next(valor);
  }

  aplicarFiltros() {
    const q = (this.textoBuscar || '').trim().toLowerCase();
    this.filtrados = this.registros.filter(r => {
      if (this.filtroFuente && (r.fuente || '') !== this.filtroFuente) return false;
      if (!q) return true;
      return [String(r.id), r.docVentas, r.cliente, r.nombreCli, r.agencia, r.nombreAgencia, r.vendedor, r.nombreVend, r.causa]
        .some(v => (v || '').toString().toLowerCase().includes(q));
    });
    this.pagina = 1;
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.filtrados.length / this.PAGE_SIZE));
  }

  get paginaActual(): ILogDepuracionPedido[] {
    const inicio = (this.pagina - 1) * this.PAGE_SIZE;
    return this.filtrados.slice(inicio, inicio + this.PAGE_SIZE);
  }

  get rangoMostrado(): string {
    if (!this.filtrados.length) return `Mostrando 0 de ${this.registros.length} registros`;
    const inicio = (this.pagina - 1) * this.PAGE_SIZE + 1;
    const fin = Math.min(this.pagina * this.PAGE_SIZE, this.filtrados.length);
    return `Mostrando ${inicio} - ${fin} de ${this.filtrados.length} registros`;
  }

  cambiarPagina(delta: number) {
    this.irPagina(this.pagina + delta);
  }

  irPagina(n: number) {
    if (n >= 1 && n <= this.totalPaginas) this.pagina = n;
  }

  /** Números de página a mostrar; null representa "..." */
  get paginas(): (number | null)[] {
    const total = this.totalPaginas;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const ini = Math.max(2, this.pagina - 2);
    const fin = Math.min(total - 1, this.pagina + 2);
    const nums: (number | null)[] = [1];
    if (ini > 2) nums.push(null);
    for (let i = ini; i <= fin; i++) nums.push(i);
    if (fin < total - 1) nums.push(null);
    nums.push(total);
    return nums;
  }

  esConcluido(r: ILogDepuracionPedido): boolean {
    return r.concluido === true || (r.estado || '').toLowerCase() === this.ESTADO_CONCLUIDO.toLowerCase();
  }

  trackById(_: number, r: ILogDepuracionPedido) {
    return r.id;
  }

  /************** MODAL PLAN DE ACCION ******** */
  abrirPlan(r: ILogDepuracionPedido) {
    this.seleccionado = r;
    this.titleModalPlan = `Plan de Acción - Depuración #${r.id}`;
    this.formPlan.reset({ planAccion: r.planAccion || '', estado: this.ESTADO_CONCLUIDO });
    if (this.esConcluido(r)) this.formPlan.disable(); else this.formPlan.enable();
    document.getElementById('abrirModalPlan').click();
  }

  cerrarPlan() {
    document.getElementById('cerrarModalPlan').click();
  }

  guardarPlan() {
    if (!this.seleccionado || this.formPlan.invalid || this.esConcluido(this.seleccionado)) return;
    const id = this.seleccionado.id;
    const { planAccion, estado } = this.formPlan.value;
    this.guardando = true;

    this.logisticaApi.updateDepuracionPlanAccion(id, { planAccion: planAccion.trim(), estado, usuario: this.usuario }).subscribe(
      (res: ILogDepuracionPedido) => {
        this.guardando = false;
        if (estado === this.ESTADO_CONCLUIDO && this.filtroEstado === this.ESTADO_PENDIENTE) {
          this.registros = this.registros.filter(x => x.id !== id);
        } else {
          this.registros = this.registros.map(x => x.id === id
            ? { ...x, ...(res || {}), planAccion, estado, concluido: estado === this.ESTADO_CONCLUIDO } : x);
        }
        this.aplicarFiltros();
        this.cerrarPlan();
        this.utilServices.mensajeOKToast('Plan de Acción registrado correctamente', 2500);
      },
      (err) => {
        this.guardando = false;
        this.utilServices.mensajeErrorVisto(err?.error?.message || 'No se pudo registrar el plan de acción', true, 4000);
      }
    );
  }

  /************** MODAL CARGA MASIVA ******** */
  abrirCarga() {
    this.limpiarCarga();
    document.getElementById('abrirModalCarga').click();
  }

  cerrarCarga() {
    document.getElementById('cerrarModalCarga').click();
  }

  limpiarCarga() {
    this.archivoCsv = null;
    this.textoCsv = '';
    this.resultadoCarga = null;
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  onFileSelect(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length) this.tomarArchivo(files[0]);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.arrastrando = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.tomarArchivo(file);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.arrastrando = true;
  }

  private tomarArchivo(file: File) {
    if (!/\.csv$/i.test(file.name)) {
      this.utilServices.mensajeErrorVisto('El tipo de archivo no es válido, solo se permite "CSV"', true, 4000);
      if (this.fileInput) this.fileInput.nativeElement.value = '';
      return;
    }
    this.archivoCsv = file;
  }

  get cargaValida(): boolean {
    return !!this.archivoCsv || this.validarTextoCsv() === null;
  }

  /** Devuelve mensaje de error de sintaxis o null si el texto CSV es válido / vacío con archivo. */
  validarTextoCsv(): string | null {
    const texto = (this.textoCsv || '').trim();
    if (!texto) return this.archivoCsv ? null : 'Debe seleccionar un archivo o pegar el contenido CSV';
    const lineas = texto.split(/\r?\n/).filter(l => l.trim());
    const cab = lineas[0].split(',').map(c => c.trim().toUpperCase());
    if (!cab.includes('ID') || !cab.includes('PLAN_DE_ACCION')) return 'La cabecera debe contener las columnas ID y PLAN_DE_ACCION';
    if (lineas.length < 2) return 'El CSV no contiene filas de datos';
    return null;
  }

  procesarCarga() {
    const error = this.archivoCsv ? null : this.validarTextoCsv();
    if (error) {
      this.utilServices.mensajeErroToast(error);
      return;
    }
    const formData = new FormData();
    if (this.archivoCsv) formData.append('file', this.archivoCsv, this.archivoCsv.name);
    else formData.append('texto', this.textoCsv.trim());
    formData.append('usuario', this.usuario);

    this.procesandoCarga = true;
    this.resultadoCarga = null;
    this.logisticaApi.cargaMasivaDepuracion(formData).subscribe(
      (res: ICargaMasivaResultado) => {
        this.procesandoCarga = false;
        this.resultadoCarga = res;
        const msg = `Registros actualizados: ${res?.actualizados ?? 0} de ${res?.procesados ?? 0}`;
        if (res?.errores?.length) this.utilServices.mensajeErroToast(`${msg}. Revise los errores por fila.`, 4000);
        else {
          this.utilServices.mensajeOKToast(msg, 2500);
          this.cerrarCarga();
        }
        this.listar();
      },
      (err) => {
        this.procesandoCarga = false;
        this.utilServices.mensajeErrorVisto(err?.error?.message || 'Error al procesar la carga masiva', true, 4000);
      }
    );
  }

  /************** EXPORTAR ******** */
  exportar() {
    this.exportando = true;
    //no se envia texto a Buscar this.textoBuscar, simepre debe descargar los pendientes
    this.logisticaApi.exportarDepuracion('', this.filtroEstado === this.ESTADO_PENDIENTE).subscribe(
      (blob: Blob) => {
        this.exportando = false;
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = `depuracion_pedidos_${new Date().toISOString().slice(0, 10)}.csv`;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        this.utilServices.mensajeOKToast('Registros descargados correctamente', 2000);
      },
      (err) => {
        this.exportando = false;
        console.error('Error exportando depuración', err);
        this.utilServices.mensajeErroToast('No se pudo descargar el archivo');
      }
    );
  }
}
